/**
 * Copyright 2023-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Col } from "react-awesome-styled-grid";
import styled, { css } from "styled-components";
import { Box, FlexContainer, theme } from "@nordcloud/gnui";
import { MOBILE_MAX_WIDTH } from "~/constants";

type Props = {
  hasPadding?: boolean;
};

const EVENTS_GRID_MIN_WIDTH = "58.5rem";
/** Viewport below this uses card rows; above uses desktop grid (grid + sidebar/chrome). */
const EVENTS_TABLE_COMPACT_MAX_WIDTH = `calc(${EVENTS_GRID_MIN_WIDTH} + 6rem)`;
const DESKTOP_MIN_WIDTH = `${theme.breakpoints.md}px`;

const constrainToContainer = css`
  min-width: 0;
  max-width: 100%;
`;

const fullWidth = css`
  width: 100%;
  ${constrainToContainer};
`;

const eventsTableCompactSizing = css`
  min-width: 0;
  width: 100%;
`;

const mobileCardPadding = css`
  padding: ${theme.spacing.spacing03} ${theme.spacing.spacing04}
    ${theme.spacing.spacing03} ${theme.spacing.spacing02};
`;

const mobileGridArea = (child: number, area: string) => css`
  > *:nth-child(${child}) {
    grid-area: ${area};
  }
`;

const eventsTableDesktopWidth = css`
  min-width: ${EVENTS_GRID_MIN_WIDTH};
  width: 100%;
  box-sizing: border-box;

  @media (max-width: ${EVENTS_TABLE_COMPACT_MAX_WIDTH}) {
    ${eventsTableCompactSizing}
  }
`;

const desktopGridColumns = css`
  display: grid;
  grid-template-columns: 2rem 3rem minmax(8rem, 1fr) 15rem 15rem 8.5rem 11rem 4rem;
  min-width: ${EVENTS_GRID_MIN_WIDTH};
`;

const mobileGridColumns = css`
  display: grid;
  grid-template-columns: 1.75rem 2rem minmax(0, 1fr) auto;
  grid-template-areas:
    "expand status name actions"
    ". start start start"
    ". end end end"
    ". duration duration duration";
  ${eventsTableCompactSizing}
  gap: ${theme.spacing.spacing02};
  align-items: center;

  ${mobileGridArea(1, "expand")}
  ${mobileGridArea(2, "status")}

  > *:nth-child(3) {
    grid-area: name;
    min-width: 0;
  }

  ${mobileGridArea(4, "start")}
  ${mobileGridArea(5, "end")}
  ${mobileGridArea(6, "duration")}

  > *:nth-child(7),
  > *:nth-child(8) {
    grid-area: actions;
    justify-self: end;
    align-self: center;
  }

  > *:nth-child(7):empty {
    display: none;
  }
`;

const gridColumnsCompact = css`
  @media (max-width: ${EVENTS_TABLE_COMPACT_MAX_WIDTH}) {
    ${mobileGridColumns}
    ${mobileCardPadding}
  }
`;

const eventDetailRowCompact = css`
  @media (max-width: ${EVENTS_TABLE_COMPACT_MAX_WIDTH}) {
    gap: ${theme.spacing.spacing03};

    &::before {
      content: attr(data-label);
      flex-shrink: 0;
      font-size: ${theme.fontSizes.sm};
      font-weight: ${theme.fontWeights.medium};
      color: ${theme.color.text.text02};
      min-width: 4.75rem;
    }
  }
`;

const filterStackDesktop = css`
  @media (min-width: ${DESKTOP_MIN_WIDTH}) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }
`;

const filterGroupDesktop = css`
  @media (min-width: ${DESKTOP_MIN_WIDTH}) {
    flex-direction: row;
    align-items: center;
    width: auto;
  }
`;

const filterControlsDesktop = css`
  @media (min-width: ${DESKTOP_MIN_WIDTH}) {
    flex-wrap: nowrap;
    width: auto;
  }
`;

export const EventsPageCol = styled(Col)`
  ${constrainToContainer}
`;

export const EventsPageBox = styled.div`
  ${fullWidth}
`;

export const EventsTableScrollContainer = styled.div`
  ${fullWidth}
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${EVENTS_TABLE_COMPACT_MAX_WIDTH}) {
    overflow-x: visible;
  }
`;

export const EventsTableInner = styled.div`
  display: inline-block;
  min-width: 100%;
  width: max-content;

  @media (max-width: ${EVENTS_TABLE_COMPACT_MAX_WIDTH}) {
    display: block;
    ${eventsTableCompactSizing}
  }
`;

export const EventsTableHeaderRow = styled.div`
  ${eventsTableDesktopWidth}
  background-color: ${theme.color.background.ui01};
  border-radius: ${theme.radiusDefault} ${theme.radiusDefault} 0 0;
`;

export const EventsTableRow = styled(Box)`
  ${eventsTableDesktopWidth}
`;

export const EventsFilterStack = styled(FlexContainer)`
  flex-direction: column;
  align-items: stretch;
  width: 100%;
  gap: ${theme.spacing.spacing04};
  ${filterStackDesktop}
`;

export const EventsFilterGroup = styled(FlexContainer)`
  flex-direction: column;
  align-items: flex-start;
  gap: ${theme.spacing.spacing03};
  ${constrainToContainer}
  width: 100%;
  ${filterGroupDesktop}
`;

/** Wraps one or more MultipleSelect groups; keeps segmented controls intact. */
export const EventsFilterControlsWrap = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: ${theme.spacing.spacing03};
  ${constrainToContainer}

  ul {
    display: inline-flex;
    flex-wrap: nowrap;
    width: auto;
    max-width: 100%;
  }

  li {
    flex: 0 0 auto;
  }

  li > button {
    width: auto;
    min-width: 6rem;
  }

  @media (max-width: ${MOBILE_MAX_WIDTH}px) {
    flex-direction: column;
    align-items: stretch;
    width: 100%;

    > * {
      width: 100%;
    }

    ul {
      display: flex;
      flex-wrap: nowrap;
      width: 100%;
    }

    li {
      display: flex;
      flex: 1 1 0;
      min-width: 0;
    }

    li > button {
      width: 100%;
      min-width: 0;
    }
  }

  ${filterControlsDesktop}
`;

export const GridColumns = styled.div<Props>`
  ${desktopGridColumns}
  padding: ${(props) => (props.hasPadding ? theme.spacing.spacing03 : "0")};
  ${gridColumnsCompact}
`;

export const EventsGridHeader = styled(GridColumns)`
  @media (max-width: ${EVENTS_TABLE_COMPACT_MAX_WIDTH}) {
    display: none;
  }
`;

export const EventDetailRow = styled(FlexContainer)`
  align-items: center;
  min-width: 0;
  ${eventDetailRowCompact}
`;
